import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Leaf, Check } from "lucide-react";

const PROFESSIONS = [
  "Sophrologue", "Naturopathe", "Hypnothérapeute", "Ostéopathe", "Réflexologue",
  "Coach bien-être", "Masseur(se)", "Énergéticien(ne)", "Praticien(ne) Reiki",
  "Praticien(ne) EFT", "Nutritionniste", "Diététicien(ne)", "Psychothérapeute", "Autre",
];

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", profession: "Sophrologue",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Mot de passe trop court (6 caractères minimum).");
      return;
    }
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) navigate("/subscribe");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-10" style={{ backgroundColor: "var(--bg)" }}>
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-10" data-testid="register-brand">
            <div className="w-10 h-10 rounded-2xl bg-sage flex items-center justify-center">
              <Leaf className="w-5 h-5 text-white" strokeWidth={1.6} />
            </div>
            <div>
              <div className="font-display">Mon Cabinet Bien-être</div>
              <div className="text-xs text-muted-soft">by Capsule Patrimoine</div>
            </div>
          </Link>

          <h1 className="font-display text-3xl mb-2">Créer mon compte</h1>
          <p className="text-muted-soft mb-8">19,99 €/mois — Garantie satisfait ou remboursé 14 jours.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-2">Votre nom</label>
              <input data-testid="register-name" type="text" required value={form.full_name} onChange={set("full_name")} placeholder="Marie Dupont" className="input-soft" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Votre profession</label>
              <select data-testid="register-profession" value={form.profession} onChange={set("profession")} className="input-soft">
                {PROFESSIONS.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Email</label>
              <input data-testid="register-email" type="email" required value={form.email} onChange={set("email")} placeholder="vous@cabinet.fr" className="input-soft" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Mot de passe</label>
              <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="6 caractères minimum" className="input-soft" />
            </div>

            {error && (
              <div className="text-sm rounded-xl px-4 py-3" style={{ backgroundColor: "#FBEEE8", color: "#8B4D3D" }} data-testid="register-error">{error}</div>
            )}

            <button data-testid="register-submit" type="submit" disabled={loading} className="btn-sage w-full text-base py-3.5">
              {loading ? "Création…" : "Créer mon compte"}
            </button>
          </form>

          <div className="text-xs text-muted-soft mt-6">
            En vous inscrivant, vous acceptez les conditions d'abonnement (19,99 € TTC/mois,
            sans engagement, résiliable à tout moment). <b>Garantie satisfait ou remboursé
            pendant 14 jours</b> sur simple email à
            contact@capsulepatrimoine.com.
          </div>

          <div className="text-sm text-muted-soft mt-8 text-center">
            Déjà inscrit(e) ? <Link to="/login" className="text-sage font-medium hover:underline" data-testid="link-login">Connectez-vous</Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-col justify-between p-12 relative" style={{ backgroundColor: "var(--sidebar)" }}>
        <div>
          <div className="text-sm text-sage font-medium mb-3">Pourquoi nos praticiens nous adorent</div>
          <h2 className="font-display text-3xl mb-6">Simplifier votre gestion quotidienne.</h2>
          <ul className="space-y-4">
            {[
              "Plus jamais de calcul d'URSSAF dans la nuit.",
              "Vos factures avec les mentions légales en 1 clic.",
              "Une vue claire sur votre activité, sans Excel.",
              "Conçu pour vous, pas pour des comptables.",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-sage flex items-center justify-center flex-shrink-0 mt-1">
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                </div>
                <span className="text-sm">{line}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl bg-white p-6 border" style={{ borderColor: "var(--border)" }}>
          <div className="text-sm leading-relaxed mb-3">
            "Je ne pensais pas qu'un outil de gestion pouvait être aussi apaisant.
            Je pose mon téléphone et tout est à jour."
          </div>
          <div className="text-xs text-muted-soft">— Léa, Sophrologue à Lyon</div>
        </div>
      </div>
    </div>
  );
}
